<script setup lang="ts">
import {computed, ref, watch, onActivated, type ComputedRef} from 'vue';
import {useI18n} from 'vue-i18n';
import {useRouter} from 'vue-router';
import {useDashboardStore, useGeneralPurposeModalStore, useLocalizationStore, useSessionStore} from 'src/store';
import {ClientFeatureType, TaskType, PIQSetupStatus} from 'src/constants/enums';
import {filterAndSortTasks} from '@/utils/arrayUtil.ts';
import TheTodaysTasksCategory from 'src/pages/DashboardHome/components/TheTodaysTasksCategory.vue';
import TheTodaysTasksPriority from 'src/pages/DashboardHome/components/TheTodaysTasksPriority.vue';
import {createDecisionModal} from 'src/utils/generalPurposeModalFactory';
import {toUserFacingDate} from 'src/utils/dateUtil';
import Box from 'src/components/BoxContainer.vue';
import Svg from '@/components/Svg.vue';
import {pain, routines} from '@/assets';
import CohortComparisonWidget from '@/pages/DashboardHome/components/CohortComparisonWidget.vue';
import PIQSetupWidget from '@/pages/DashboardHome/components/PIQSetupWidget.vue';
import {dismisscanaryactivation} from 'src/api/endpoints/patientWeb';
import {handleApiRequest} from '@/api/handleApiRequest';
import {getPatientHydrate} from '@/api/endpoints/userWeb';
import TaskCompletionProgress from '@/pages/DashboardHome/components/TaskCompletionProgress.vue';
import ThePatientSummaryCard from "@/components/ThePatientSummaryCard.vue";
import {useAiSummariesStore} from "@/store/aiSummariesStore.ts";

const dashboardStore = useDashboardStore();
const {t} = useI18n();
const sessionStore = useSessionStore();
const localizationStore = useLocalizationStore();
const router = useRouter();
const generalPurposeModalStore = useGeneralPurposeModalStore();
const aiSummariesStore = useAiSummariesStore();

const eocId: ComputedRef<number | undefined> = computed(() => sessionStore.selectedProcedure?.eocId);
const isSurgicalProcedure = computed(() => sessionStore.selectedProcedure?.isSurgicalProcedure ?? false)
const isPIQandDisabled = computed(() => dashboardStore.patientConfig?.isDischarged);
const isWaitListFeatureEnabled = computed(() => dashboardStore.patientConfig?.features?.isWaitlistEnabled ?? false);
const seldEocOnWaitlist = computed(() => sessionStore.selectedProcedure?.onWaitlist ?? false);
const isLoading = computed(() => dashboardStore.isLoading);
const taskType = computed(() => TaskType);
const lastPainScore = computed(() => dashboardStore.patient?.painAndOpioidData.lastPainScore);
const progressWidget = computed(() => dashboardStore.patient?.progressWidget);
const canSelfReportPainRating = computed(() => isPostSurgery.value && !isSelfReportLocked.value);
const isSelfReportLocked = computed(() => !dashboardStore.patient?.painAndOpioidData?.checkinAllowed);
const todaysDate = computed(() => dashboardStore.todaysDate);

const progressInfoLabelData = ref<string | null>(null);

const aiSummaries = computed(() => aiSummariesStore.summaries.slice(0, 5)),
  hasAiSummaries = computed(() => dashboardStore.patientConfig?.features.isAIPatientSummariesEnabled ?? false),
  hasCohortComparisons = computed(() => dashboardStore.patientConfig?.features.patientProgressFeatureConfig.isProgressWidgetVisible ?? false),
  progressInfoLabel = computed(() => progressInfoLabelData.value);

const lastPainRatingDate = computed(() => {
  const date = dashboardStore.patient?.painAndOpioidData.lastCheckin;
  if (date) {
    return toUserFacingDate(date, false, localizationStore.locale);
  }
  return t('pain_rating_empty');
});

const isAdHocPainTrackingEnabled = computed(() => {
  let result = false;
  if (sessionStore && sessionStore.user && sessionStore.user.clientFeatures) {
    result = sessionStore.user.clientFeatures.includes(ClientFeatureType.PainTracking) && isPostSurgery.value;
  }
  return result;
});

const isPostSurgery = computed(() => {
  let result = false;
  if (dashboardStore && dashboardStore.patient && dashboardStore.patient.daysSinceUntilSurgery) {
    result = dashboardStore.patient.daysSinceUntilSurgery as number > 0;
  }
  return result;
});

const hasPersonaIQ = computed(() => {
  return dashboardStore?.patient?.hasPersonaIQ === true;
});

const piqSetupStatus = computed((): PIQSetupStatus => {
  return dashboardStore?.patient?.personaIQSetupStatus ?? PIQSetupStatus.Undefined;
});

const displayEducations = computed(() => dashboardStore?.patientConfig?.educations?.isCategoryEnabled ?? false);
const displaySurveys = computed(() => dashboardStore?.patientConfig?.surveys?.isCategoryEnabled ?? false);
const displayRoutines = computed(() => dashboardStore?.patientConfig?.routines?.isCategoryEnabled ?? false);

const displayEducationAdherence = computed(() => dashboardStore?.patientConfig?.educations?.isAdherenceEnabled ?? false);
const displaySurveyAdherence = computed(() => dashboardStore?.patientConfig?.surveys?.isAdherenceEnabled ?? false);
const displayRoutineAdherence = computed(() => dashboardStore?.patientConfig?.routines?.isAdherenceEnabled ?? false);
const displayTaskCompletion = computed(() => displayEducationAdherence.value || displaySurveyAdherence.value || displayRoutineAdherence.value);

const educationTasks = computed(() => {
  const tasks = dashboardStore.tasks;
  return filterAndSortTasks(TaskType.education, tasks);
});

const surveyTasks = computed(() => {
  const tasks = dashboardStore.tasks;
  return filterAndSortTasks(TaskType.survey, tasks);
});

const routineTasks = computed(() => {
  const tasks = dashboardStore.tasks;
  return filterAndSortTasks(TaskType.routine, tasks);
});

const showTaskCompletionDlg = ref<boolean>(false);

const taskCompletionSubTitle: ComputedRef<string> = computed(() => {

  // Surgical
  if (isSurgicalProcedure.value) {
    return isPostSurgery.value ? t('education_post_surgery') : t('education_pre_surgery');

  // Non-Surgical
  } else {
    return isPostSurgery.value ? t('post_treatment') : t('pre_treatment');
  }
});

const daysSinceUntilSurgery = computed(() => {
  let result: number | null = null;
  if (dashboardStore && dashboardStore.patient && dashboardStore.patient.daysSinceUntilSurgery !== undefined) {
    result = dashboardStore.patient.daysSinceUntilSurgery;
  }
  return result;
});

const daysUntilSinceSurgeryQualifier = computed(() => {
  let result = '';

  const days: number | null = daysSinceUntilSurgery.value;
  
  if (!isSurgicalProcedure.value) {
    result = t('days_on_program_pluralization')
  } else if (days !== null) {
    if (days > 0) {
      result = (days === 1 ? t('days_since_surgery_pluralization_one') : t('days_since_surgery_pluralization_many')).toString();
    } else if (days < 0) {
      result = (days === 1 ? t('days_until_surgery_pluralization_one') : t('days_until_surgery_pluralization_many')).toString();
    } else {
      result = t('days_until_surgery_pluralization_many').toString();
    }
  }
  return result;
});

const dayString = computed(() => {
  if (daysSinceUntilSurgery.value !== null) {
    return (isSurgicalProcedure.value ? Math.abs(daysSinceUntilSurgery.value) : daysSinceUntilSurgery.value).toString();
  }
  
  return '';
});

const taskCompletionDlgDescStr = computed(() => isSurgicalProcedure.value ? t('task_completion_surgery_modal') : t('task_completion_treatment_modal'));

function onAddEditSurgeryDateClick() {
  router.push({ name: 'Procedure.SetSurgeryDate' });
}

function onPainRatingClicked() {
  if (canSelfReportPainRating.value) {
    router.push({ name: 'PainRating' });
  } else if (isSelfReportLocked.value) {
    const descString = t('pain_rating_lock_modal_text')
      .toString()
      .replace(/\\n/gi, '<br />')
      .replace(/\\/gi, '');
    const info = createDecisionModal(
      t('pain_rating_lock_modal_title').toString(),
      descString
    );
    generalPurposeModalStore.setModal(info);
  }
}

function piqSetupWidgetOnClose() {
        dashboardStore.patient!.personaIQSetupStatus = PIQSetupStatus.Completed;
}

watch(piqSetupStatus, async (newVal, oldVal) => {
  // When Segment 3 is completed, we call the dismiss endpoint to hide the widget on the next login
  if(newVal === PIQSetupStatus.CompletedSegment3) {
    dashboardStore.setIsLoading(true);
    await dismisscanaryactivation().finally(() => { dashboardStore.setIsLoading(false); });
  }
});

onActivated(async () => {
  const patientHydrate = await handleApiRequest(() => getPatientHydrate(), null);
  if (patientHydrate?.result?.patientProgress?.mobilityProgress?.progressInfoLabel) {
    progressInfoLabelData.value = patientHydrate.result.patientProgress.mobilityProgress.progressInfoLabel;
  }
});
</script>

<template>
  <div v-if="hasPersonaIQ && piqSetupStatus !== PIQSetupStatus.Completed" class="mb-30" data-testid="piqSetupWidget">
    <PIQSetupWidget :setupStatus="piqSetupStatus" @onClose="piqSetupWidgetOnClose"/>
  </div>

  <!-- If patient is NOT PIQ & discharged. -->
  <div v-if="!isPIQandDisabled">

    <!-- Heading -->
    <div class="row items-center dash-home__heading">

      <!-- Title -->
      <div class="col">
        <h1>
          <zbm-skeleton-loader height="34px"
                               :loading="isLoading"
                               width="100%">
            {{ t('home_tasks') }}
          </zbm-skeleton-loader>
        </h1>
      </div>
      <div class="flex-br" />

      <!-- Date -->
      <div class="col-auto">
        <zbm-skeleton-loader height="24px"
                             :loading="isLoading"
                             width="100px">
          {{ todaysDate }}
        </zbm-skeleton-loader>
      </div>
    </div>

     <div v-if="isWaitListFeatureEnabled && seldEocOnWaitlist" class="row justify-center q-pt-sm">
          <div class="col self-end">
            <zbm-btn
              :color="$zb.enums.ButtonStdColorEnum.accentSecondary"
              :outline="true"
              :width="$zb.enums.ButtonWidthEnum.wFull"
              :height="$zb.enums.ButtonHeightEnum.lgNoPadding"
              @click="onAddEditSurgeryDateClick"
            >
              <template #default>
                <div class="dash-home__surgery-date-btn">
                  <span>
                    <q-icon name="fa-solid fa-bell" size="20px" />
                  </span>
                  <span>
                      {{ t('add_surgery_date') }}
                  </span>
                  <span>
                    <q-icon name="fa-solid fa-chevron-right" size="20px" />
                  </span>
                </div>
              </template>
            </zbm-btn>
          </div>
        </div>

    <div class="row dash-home__tasks q-pt-sm">
      <div class="col">
        <!-- Education Tasks -->
        <zbm-skeleton-loader class="mb-18"
                             height="70px"
                             :loading="isLoading"
                             width="100%"
                             v-if="displayEducations">
          <TheTodaysTasksCategory :label="t('gen_education')"
                                  :on-wait-list="seldEocOnWaitlist"
                                  :tasks="educationTasks"
                                  :taskType="taskType.education"
                                  data-testid="educationTasks">
            <template #icon>
              <q-icon name="fa-solid fa-graduation-cap"
                      size="27px" />
            </template>
          </TheTodaysTasksCategory>
        </zbm-skeleton-loader>

        <!-- Surveys Category -->
        <zbm-skeleton-loader class="mb-18"
                             height="70px"
                             :loading="isLoading"
                             width="100%"
                             v-if="displaySurveys">
          <TheTodaysTasksCategory :label="t('gen_surveys')"
                                  :on-wait-list="seldEocOnWaitlist"
                                  :tasks="surveyTasks"
                                  :taskType="taskType.survey"
                                  data-testid="surveyTasks">
            <template #icon>
              <q-icon name="fa-solid fa-clipboard-list"
                      size="29px" />
            </template>
          </TheTodaysTasksCategory>
        </zbm-skeleton-loader>

        <!-- Routines Category -->
        <zbm-skeleton-loader height="70px"
                             :loading="isLoading"
                             width="100%"
                             v-if="displayRoutines">
          <TheTodaysTasksCategory :label="t('gen_routines')"
                                  :tasks="routineTasks"
                                  :taskType="taskType.routine"
                                  data-testid="routineTasks">
            <template #icon>
              <Svg :view-box="routines.viewBox"
                   :vector-paths="routines.vectorPaths" />
            </template>
          </TheTodaysTasksCategory>
        </zbm-skeleton-loader>
      </div>
    </div>

    <!-- My Recovery Progress -->
    <div v-if="hasAiSummaries || hasCohortComparisons" 
         class="row dash-home__recovery-prog"
         data-testid="myRecoveryProgress">
      <div class="col">

        <!-- Title -->
        <h3 class="mb-16">{{ t('my_recovery_progress') }}</h3>

        <!-- MEOC Progress Warning -->
        <q-banner v-if="progressInfoLabel"
                  class="bg-primitives-action-10 text-primitives-action-100 q-mb-md"
                  dense
                  rounded>
          
          <!-- Icon -->
          <template #avatar>
            <q-icon name="fa-solid fa-triangle-exclamation"
                    size="18px" />
          </template>
          
          <!-- Message -->
          {{ progressInfoLabel }}
        </q-banner>
        
        <!-- Patient Summaries -->
        <div v-if="hasAiSummaries"
             class="pat-sums">
          
          <!-- Section Sub-Header -->
          <div class="row mb-12">
            
            <!-- Sub-Title -->
            <div class="col">
              <p class="text-weight-semibold text-content-txt-subtitle">{{ t('patient_dashboard_recovery_updates') }}</p>
            </div>
            
            <!-- View All -->
            <div class="col-auto">
              <zbm-btn-link v-if="eocId"
                            align-w-text
                            icon-right="fa-solid fa-angle-right"
                            :label="t('eoc_view_all')"
                            text-color="content-txt-subtitle"
                            :to="{ name: 'RecoveryUpdates.List', params: {eocId: eocId} }" />
            </div>
          </div>

          <!-- Summary Card(s) -->
          <div class="row no-wrap pat-sums__cards">
            <div v-for="(sum, i) in aiSummaries"
                 :key="i"
                 class="col-auto">
              <ThePatientSummaryCard :delivery-date="sum.deliveryDate"
                                     :eoc-id="eocId"
                                     :index="i"
                                     :sum-id="sum.id"
                                     :title="sum.title"
                                     :viewed-date="sum.viewedDate" />
            </div>
          </div>
        </div>

        <!-- Cohort Comparison Widget -->
        <CohortComparisonWidget v-if="hasCohortComparisons"
                                :data-status="progressWidget?.dataCollectingStatus"
                                :data-string="progressWidget?.dataAvailableDisplayString"
                                :metrics="progressWidget?.metrics" />
      </div>
    </div>

    <!-- Today's Tasks Priority - Pain Rating  -->
    <div v-if="isAdHocPainTrackingEnabled && !canSelfReportPainRating"
         class="row dash-home__priority"
         data-testid="painRatingDiv">
      <div class="col">
        <TheTodaysTasksPriority :click="onPainRatingClicked"
                                :label="t('pain_rating_title')"
                                :loading="isLoading"
                                :locked="isSelfReportLocked"
                                :dataTestId="`painRatingPriority`">

          <!-- Icon -->
          <template #icon>
            <Svg :view-box="pain.viewBox"
                 :vector-paths="pain.vectorPaths" />
          </template>

          <!-- Label -->
          <template #default>
            <span v-if="lastPainScore"
                  class="text-weight-bold">
              {{ `${lastPainScore}/10` }}&nbsp;&nbsp;
            </span>
            <span class="text-content-trg-secondary-def">{{ lastPainRatingDate }}</span>
          </template>
        </TheTodaysTasksPriority>
      </div>
    </div>

    <!-- Today's Tasks Priority - Daily Walk Goal -->
    <!--    <div class="row dash-home__priority">
          <div class="col col-sm-6">
            <TheTodaysTasksPriority :label="t('gait_daily_walk_goal')">

              &lt;!&ndash; Icon &ndash;&gt;
              <template #icon>
                <q-icon color="primary"
                        name="fa-solid fa-walking"
                        size="31px" />
              </template>

              &lt;!&ndash; Label &ndash;&gt;
              <template #default>
                <span class="text-weight-bold">{{ numberOfSessions }}</span> {{ t('gait_sessions') }},<br />
                <span class="text-weight-bold">{{ timeOfSessions }}</span> {{ t('gait_minutes_each') }}
              </template>
            </TheTodaysTasksPriority>
          </div>
        </div>-->
    <!-- Video Visit Coming Up -->
    <!--    <div class="row dash-home__priority">
          <div class="col ">
            <TheTodaysTasksPriority class="coming-up"
                                    :label="t('videovisit_comingup_heading')">

              &lt;!&ndash; Icon &ndash;&gt;
              <template #icon>
                <q-icon color="primary"
                        name="fa-regular fa-calendar-alt"
                        size="31px" />
              </template>

              &lt;!&ndash; Label &ndash;&gt;
              <template #default>
                <span>{{ t('videovisit_title') }}</span><span class="text-dark-teal">{{ videoVisitDate }}</span>
              </template>
            </TheTodaysTasksPriority>
          </div>
        </div>-->
    
    <!-- Task Completion -->
    <div v-if="displayTaskCompletion"
         class="row dash-home__completion"
         data-testid="myProgress">
      <div class="col">

        <!-- Heading -->
        <div class="row items-start dash-home__completion-heading">

          <!-- Title -->
          <div class="col-auto title">
            <zbm-skeleton-loader height="57px"
                                 :loading="isLoading"
                                 width="247px">
              <h2>
                
                <!-- Title -->
                {{ t('task_completion_title') }}

                <!-- Info -->
                <zbm-btn-icon @click="showTaskCompletionDlg = !showTaskCompletionDlg"
                              icon="fa-regular fa-circle-info"
                              size="16px"
                              text-color="primitives-foundation-50" />
              </h2>
              
              <!-- Sub-Title -->
              <p class="text-primitives-foundation-50 text-weight-semibold">{{ taskCompletionSubTitle }}</p>
            </zbm-skeleton-loader>
            
            <!-- Info Dialog -->
            <zbm-dialog v-model="showTaskCompletionDlg">
              
              <!-- Header -->
              <div class="row q-dialog__header">
                <div class="col-auto">

                  <!-- Question Circle Icon -->
                  <q-icon color="content-assets-hero-def"
                          name="fa-solid fa-circle-info"
                          size="42px" />
                </div>
              </div>

              <!-- Body -->
              <div class="row q-dialog__body">
                <div class="col">
                  <h3 class="text-weight-semibold q-mb-lg">{{ t('task_completion_title') }}</h3>
                  <p>{{ taskCompletionDlgDescStr }}</p>
                </div>
              </div>

              <!-- Footer (CTA button(s)) -->
              <div class="row q-dialog__footer">

                <!-- Okay Button -->
                <div class="col-auto">
                  <zbm-btn v-close-popup
                           :color="$zb.enums.ButtonStdColorEnum.accentText"
                           :height="$zb.enums.ButtonHeightEnum.lg"
                           :label="t('gen_okay')" />
                </div>
              </div>
            </zbm-dialog>
          </div>
          
          <!-- Days -->
          <template v-if="!seldEocOnWaitlist">
            <div class="flex-br" />
            <div class="col days">
              <zbm-skeleton-loader height="38px"
                                   :loading="isLoading"
                                   width="100%">
                <h2 class="text-body">{{ dayString }} <span>{{ daysUntilSinceSurgeryQualifier }}</span></h2>
              </zbm-skeleton-loader>
            </div>
          </template>
        </div>
        
        <!-- Education -->
        <TaskCompletionProgress v-if="displayEducationAdherence"
                                :stat-type="$zb.enums.StatisticTypeEnum.EducationProgress" />

        <!-- Assessments -->
        <TaskCompletionProgress v-if="displaySurveyAdherence"
                                :stat-type="$zb.enums.StatisticTypeEnum.SurveyProgress" />

        <!-- Routines -->
        <TaskCompletionProgress v-if="displayRoutineAdherence"
                                :stat-type="$zb.enums.StatisticTypeEnum.ExerciseProgress" />
      </div>
    </div>
  </div>

  <!-- If patient IS PIQ & discharged -->
  <div v-else
       class="row dash-home__defeatured">
    <div class="col">
      <Box :box-bkgd-color="$zb.enums.BoxBackgroundColorEnum.grey"
           :box-padding="$zb.enums.BoxPaddingEnum.p303055"
           class="text-center">
        <img src="/img/defeatured.svg" alt="" />
        <h2 class="text-secondary">{{ t('patient_discharge_stats') }}</h2>
        <p>{{ t('patient_discharge_stats_desc') }}</p>
      </Box>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use 'sass:map';
@use 'sass:math';

.dash-home {
  $dash-home-border: 1px solid $border-clr;

  // Add Surgery Date button
  &__surgery-date-btn {
    display: flex;
    align-items: center;
    width: 100%;
    position: relative;

    > :first-child {
      flex: 0 0 32px;
      display: flex;
      align-items: center;
      justify-content: flex-start;
      min-width: 32px;
    }
    > :nth-child(2) {
      flex: 1 1 0;
      text-align: left;
      font-weight: 600;
      font-size: 1.1em;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    > :last-child {
      flex: 0 0 32px;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      min-width: 32px;
    }
  }

  // Heading
  &__heading { // .row
    margin-block-end: 21px;
  }

  // Today's Tasks
  &__tasks { // .row
    margin-block-end: 20px;
  }

  // My Recovery Progress
  &__recovery-prog { // .row
    padding-block-start: 20px;
    border-block-start: $dash-home-border;
    margin-block-end: 20px;
    
    // Patient Summaries
    .pat-sums {
      padding-block-end: 20px;
      border-block-end: $dash-home-border;
      margin-block-end: 20px;
      
      // View All
      .q-btn-link { // .q-btn
        font-weight: map.get($text-weights, semibold);
        font-size: $body-font-size;
        line-height: $body-line-height;
        
        &:hover {
          text-decoration: none;
        }
      }
      
      // Cards
      &__cards { // .row
        overflow-x: auto;
        column-gap: 8px;
        padding-block: 1px 5px;
        padding-inline: 1px;
      }
    }
  }

  // Today's Tasks Priority
  &__priority { // .row
    padding-block-start: 20px;
    border-block-start: $dash-home-border;
    margin-block-end: 25px;
  }

  // Task Completion
  &__completion { // .row
    padding-block-start: 30px;
    border-block-start: $dash-home-border;
    
    &:not(:last-child) {
      margin-block-end: 30px;
    }

    // Heading
    &-heading { // .row
      margin-block-end: 44px;

      // Title
      .title { // [class*="col"]
        
        h2 {
          position: relative;
          padding-inline-end: 30px;
          margin-block-end: 0;
        }
        
        .q-btn--icon {
          position: absolute;
          top: 0;
          right: 0;
        }
      }

      // Days
      .days { // [class*="col"]
        padding-block: 5px;

        span {
          font-weight: map.get($text-weights, regular);
          font-size: map.get($font-sizes, '16');
        }
      }
    }
  }

  // Discharged PIQ Patient
  &__defeatured { // .row

    img {
      max-width: 400px;
      width: 100%;
      margin: 0 auto 30px;
    }
  }

  @media (min-width: 400px) {

    // Heading
    &__heading { // .row

      > [class*="col"] {
        padding-inline: 20px;

        &:first-child {
          padding-inline-start: 0;
        }

        &:last-child {
          padding-inline-end: 0;
        }
      }

      .flex-br {
        display: none;
      }
    }

    // Task Completion
    &__completion { // .row

      // Title
      &-heading { // .row
        padding-block-start: 5px;
        
        .title {
          padding-inline-end: 18px;
        }
        
        .flex-br {
          display: none;
        }

        // Days
        .days { // [class*="col"]
          padding-inline-start: 18px;
          border-inline-start: 1px solid $border-clr;
        }
      }
    }
  }
}
</style>
